import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  CONTRACT_TYPES,
  COUNTERPARTY_NAME_FIELD,
  DEFAULT_PAGE_LIMIT,
  QUEUE_FIELD_NAMES,
  type ContractType,
  type SelectableParty,
  type VerificationQueueItem,
  type VerificationQueueRequest,
  type VerificationQueueResponse,
  type VerificationStatus,
  type VerifyFieldsBulkResponse,
  type VerifyFieldsBulkResult,
} from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { fetchVerificationQueue, verifyFieldsBulk } from '../api/verifications';
import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { AgingSignal } from '../components/AgingSignal';
import { ContractTypeTag } from '../components/ContractTypeTag';
import { CursorPager } from '../components/CursorPager';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { ErrorNotice } from '../components/ErrorNotice';
import { OpenContractLink } from '../components/OpenContractLink';
import { PartyPicker } from '../components/PartyPicker';
import { ProvenanceChip } from '../components/ProvenanceChip';
import { SelectionToolbar } from '../components/SelectionToolbar';
import { TagInput } from '../components/TagInput';
import { verificationStatusLabel } from '../components/trust-labels';
import { VerificationStatusLabel } from '../components/VerificationBadge';
import { useSelection } from '../hooks/useSelection';
import {
  EMPTY_CURSOR_STACK,
  pageNumber,
  popCursor,
  pushCursor,
  type CursorStack,
} from '../components/cursor-stack';
import { requireSurface } from '../routes/surfaces';
import { RETIRED_COPY, askedWithCopy, isAdminFieldName, overBoundCopy, tablesMissingApplies } from './extraction-fields-shared';
import { queueFiltersFromParams, statusFilterView } from './verification-queue-params';
import { VerifyPanel } from './VerifyPanel';

/**
 * Verification queue — FR-07, CR-01, NFR-07, NFR-12.
 *
 * The read half of the ISO 42001 evidence chain: which AI-derived values are
 * still waiting for a named human to confirm or correct them, oldest first,
 * because aging is the whole point of the queue.
 *
 * ## Every filter is a request parameter, and that is not a style choice
 *
 * There is no client-side sort and no client-side filter over a page. Scope is a
 * query predicate (NFR-06) — the server's statement is incapable of returning a
 * field outside the caller's scope, and it pages *within* that row set. A
 * browser filtering the twenty rows it happens to hold would be filtering a
 * sample, and would report a total that matches neither the sample nor the
 * scope. So `high_stakes_only`, `escalated_only` and `status` are sent, the
 * server re-derives scope from the token, and the four `totals` come back
 * counted server-side.
 *
 * ## Paging is forward-only
 *
 * `next_cursor` is opaque and there is no previous cursor on the wire, so
 * Previous is a pop off a client-held stack (`components/cursor-stack.ts`).
 * Constructing a cursor would be a defect.
 *
 * ## Opened on a link: `?field_name=` and `?status=` (CLM-108, R-108-17)
 *
 * The Auto-approval page links here as `?field_name=<field>&status=AutoApproved`
 * — "N values auto-approved under earlier policy stay so — view them". Both are
 * read from the URL on mount **and whenever the query string changes under the
 * mounted page** — the rail's plain "Verification queue" link, Back and Forward
 * — so a reload or a copied link always shows what is on screen. A change the
 * page makes itself (dropping a filter the user overrode) is not re-read. Both
 * are validated against the vocabulary (`verification-queue-params.ts`): an
 * unknown value is dropped, never sent. Both are sent as the request
 * parameters the API already accepted, so the rows and the scope are still the
 * server's. Without `status`, or with `status=Pending` alone, the queue is the
 * `Pending` queue, as before.
 *
 * An `AutoApproved` row is **view only**: no selection box, never offered to the
 * bulk bar, and its panel has no verbs (the API refuses them, R-108-21). A
 * notice above the table says so — for the whole page when the filter is
 * `AutoApproved` alone, and for those rows only when it is mixed, whose other
 * rows stay reviewable. Its status column reads "Auto-approved (policy)" in
 * the `ai` tone — settled, and not by a person (CR-06).
 */

interface QueueState {
  readonly response: VerificationQueueResponse | null;
  readonly busy: boolean;
  readonly problem: { readonly kind: ApiProblemKind; readonly detail: string | null; readonly correlation_id: string | null } | null;
}

const INITIAL: QueueState = { response: null, busy: true, problem: null };

/**
 * CLM-86 — the outcome of the last bulk call, kept per row so a badge survives
 * the re-fetch that follows it (a `not_permitted` or `failed` row is still on
 * the page; a settled one has left it). Cleared on every filter or page change.
 */
interface BulkState {
  readonly busy: boolean;
  readonly results: ReadonlyMap<string, VerifyFieldsBulkResult>;
  readonly counts: VerifyFieldsBulkResponse['counts'] | null;
  readonly problem: QueueState['problem'];
}

const BULK_IDLE: BulkState = { busy: false, results: new Map(), counts: null, problem: null };

/** Roles the API admits to `field:verify` — a hint for the buttons, never the gate (CLM-82). */
const BULK_ROLES: readonly string[] = ['contributor', 'admin'];
const BULK_ROLE_HINT = 'Verifying fields needs the contributor role';
const REASON_REQUIRED =
  'A rejection needs a reason. The field stays in the queue and keeps escalating, so the next reviewer needs to know what stopped this one.';

/** One stable empty list, so `useSelection` does not reconcile against a fresh `[]` every render. */
const NO_ITEMS: readonly VerificationQueueItem[] = [];

/** `dates.expiry_date` → `Expiry date`: the path's last segment, humanised. */
function fieldLabel(fieldName: string): string {
  const last = fieldName.split('.').at(-1) ?? fieldName;
  const words = last.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const RESULT_BADGE: Readonly<Record<VerifyFieldsBulkResult['status'], { readonly label: string; readonly tone: string }>> = {
  settled: { label: 'Settled', tone: 'ok' },
  not_permitted: { label: 'Not permitted', tone: 'mut' },
  not_pending: { label: 'Not pending', tone: 'out' },
  not_found: { label: 'Not found', tone: 'out' },
  failed: { label: 'Failed', tone: 'bad' },
};

function ResultBadge({ result }: { readonly result: VerifyFieldsBulkResult }) {
  const badge = RESULT_BADGE[result.status];
  const title =
    result.status === 'not_permitted'
      ? 'Only Legal or an admin can decide the counterparty name'
      : result.status === 'failed' && result.message !== undefined
        ? result.message
        : undefined;
  return (
    <span className={`vq-result ${badge.tone}`} {...(title === undefined ? {} : { title })}>
      {badge.label}
    </span>
  );
}

export function VerificationQueue() {
  const surface = requireSurface('verify');
  const { identity } = useIdentity();
  const mayBulk = mayAttempt(identity, BULK_ROLES);

  /* CLM-108 (R-108-17) — `?field_name=` and `?status=` seed the filters on mount, and again when the
   * query string changes under the mounted page; see the module note. */
  const [params, setParams] = useSearchParams();
  const [initial] = useState(() => queueFiltersFromParams(params));
  const [status, setStatus] = useState<readonly VerificationStatus[] | null>(initial.status);
  const { onlyAutoApproved, includesAutoApproved } = statusFilterView(status);
  /** The query string the filters were last read from, or last written to by this page itself. */
  const appliedSearch = useRef(params.toString());

  const [highStakesOnly, setHighStakesOnly] = useState(false);
  const [escalatedOnly, setEscalatedOnly] = useState(false);
  /* CLM-86 — the four row narrowings, each sent as a query key; the API ANDs them after the scope predicate. */
  const [party, setParty] = useState<SelectableParty | null>(null);
  const [fieldName, setFieldName] = useState<string>(initial.fieldName);
  const [contractType, setContractType] = useState<'' | ContractType>('');
  const [tags, setTags] = useState<readonly string[]>([]);
  const [bulk, setBulk] = useState<BulkState>(BULK_IDLE);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [stack, setStack] = useState<CursorStack>(EMPTY_CURSOR_STACK);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [state, setState] = useState<QueueState>(INITIAL);
  const [openId, setOpenId] = useState<string | null>(null);
  /** Bumped to force a re-fetch of the same page — the conflict Reload path. */
  const [reloadNonce, setReloadNonce] = useState(0);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    const request: VerificationQueueRequest = {
      // Oldest first is the default in the contract too; sent explicitly so the
      // ordering the user sees is the ordering this page asked for.
      sort: 'oldest_first',
      ...(highStakesOnly ? { high_stakes_only: true } : {}),
      ...(escalatedOnly ? { escalated_only: true } : {}),
      ...(party === null ? {} : { party_key: party.party_key }),
      ...(fieldName === '' ? {} : { field_name: fieldName }),
      ...(contractType === '' ? {} : { contract_type: contractType }),
      ...(tags.length === 0 ? {} : { tags: [...tags] }),
      ...(status === null ? {} : { status: [...status] }),
      page: cursor === undefined ? { limit: DEFAULT_PAGE_LIMIT } : { limit: DEFAULT_PAGE_LIMIT, cursor },
    };

    setState((previous) => ({ ...previous, busy: true }));

    void (async () => {
      try {
        const response = await fetchVerificationQueue(request, controller.signal);
        if (controller.signal.aborted) return;
        setState({ response, busy: false, problem: null });
      } catch (error) {
        if (controller.signal.aborted) return;
        setState({
          response: null,
          busy: false,
          problem: {
            kind: classifyApiError(error),
            detail: error instanceof ApiError ? error.message : null,
            correlation_id: error instanceof ApiError ? error.correlation_id : null,
          },
        });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [highStakesOnly, escalatedOnly, party, fieldName, contractType, tags, status, cursor, reloadNonce]);

  const items = state.response?.items ?? NO_ITEMS;
  /*
   * CLM-86 (D-86-1) — keyed by verification_id; Select all is the loaded page, never `totals.pending`.
   * CLM-108 (R-108-17) — minus the rows the policy settled: they are terminal, so they are never
   * selectable and never reach the bulk bar. Memoised, because `useSelection` reconciles on identity.
   */
  const selectableItems = useMemo(
    () => items.filter((item) => item.verification_status !== 'AutoApproved'),
    [items],
  );
  const selection = useSelection(selectableItems, (item) => item.verification_id);

  /** The last bulk result belongs to the page it was made on. */
  const clearBulk = useCallback(() => {
    setBulk(BULK_IDLE);
    setRejecting(false);
    setRejectReason('');
    setRejectError(null);
  }, []);

  /** A filter change invalidates every cursor: they are minted per filter set. */
  const resetPaging = useCallback(() => {
    setStack(EMPTY_CURSOR_STACK);
    setCursor(undefined);
    setOpenId(null);
    selection.reset();
    clearBulk();
  }, [selection, clearBulk]);

  /**
   * CLM-108 — the URL seeded a filter the user has now changed: drop it from the
   * query string (merged, never replaced) so a reload or a copied link shows
   * what is on screen, not the link the page was opened from.
   */
  const dropUrlFilter = useCallback(
    (key: 'field_name' | 'status') => {
      if (!params.has(key)) return;
      setParams(
        (previous) => {
          const merged = new URLSearchParams(previous);
          merged.delete(key);
          /* This page's own write: the state already says it, so the effect below must not re-read it. */
          appliedSearch.current = merged.toString();
          return merged;
        },
        { replace: true },
      );
    },
    [params, setParams],
  );

  /*
   * CLM-108 — the query string changed under the mounted page, and not by this
   * page: the rail links to the bare path, and Back and Forward move between
   * links. react-router keeps this element mounted across a search-only
   * navigation, so without this the filters a link set would outlive the link —
   * the view-only list of policy-settled rows under a plain queue URL, which a
   * reload would then show differently. Re-read both URL filters and start the
   * paging over; the narrowings the URL never carries are the user's and stay.
   */
  useEffect(() => {
    const search = params.toString();
    if (search === appliedSearch.current) return;
    appliedSearch.current = search;
    const next = queueFiltersFromParams(params);
    setStatus(next.status);
    setFieldName(next.fieldName);
    resetPaging();
  }, [params, resetPaging]);

  function showPendingQueue() {
    setStatus(null);
    dropUrlFilter('status');
    resetPaging();
  }

  /**
   * CLM-86 — one bulk call over the selected rows. `seen_extracted_value` is what
   * each row **showed** (the `VerifyPanel` rule, N times), so a re-extracted row
   * comes back `not_pending` rather than attested to. The call is not atomic by
   * design (D-86-7): the response says which rows landed, and the surface then
   * re-fetches so settled rows leave the queue and the totals recount server-side.
   */
  const runBulk = useCallback(
    async (outcome: 'confirm' | 'reject', reason?: string) => {
      const selectedRows = selectableItems.filter((item) => selection.isSelected(item.verification_id));
      if (selectedRows.length === 0) return;
      setBulk((previous) => ({ ...previous, busy: true, problem: null }));
      try {
        const response = await verifyFieldsBulk({
          items: selectedRows.map(({ verification_id, contract_key, field_name, extracted_value }) => ({
            verification_id,
            contract_key,
            field_name,
            seen_extracted_value: extracted_value,
          })),
          outcome,
          ...(reason === undefined ? {} : { reason }),
        });
        setBulk({
          busy: false,
          results: new Map(response.results.map((result) => [result.verification_id, result])),
          counts: response.counts,
          problem: null,
        });
        setRejecting(false);
        setRejectReason('');
        setRejectError(null);
        setReloadNonce((n) => n + 1);
      } catch (error) {
        setBulk({
          busy: false,
          results: new Map(),
          counts: null,
          problem: {
            kind: classifyApiError(error),
            detail: error instanceof ApiError ? error.message : null,
            correlation_id: error instanceof ApiError ? error.correlation_id : null,
          },
        });
      } finally {
        selection.reset();
      }
    },
    [selectableItems, selection],
  );

  function onRejectSelected() {
    const reason = rejectReason.trim();
    if (reason === '') {
      setRejectError(REASON_REQUIRED);
      return;
    }
    void runBulk('reject', reason);
  }

  const openItem = items.find((item) => item.verification_id === openId) ?? null;

  const rowButtonId = (item: VerificationQueueItem) => `vq-open-${item.verification_id}`;

  /**
   * Focus is returned to the button that opened the panel, not to the top of
   * the document — a reviewer working down a queue by keyboard must land back
   * where they were (CLM-51).
   */
  const closePanel = useCallback(() => {
    const id = openId;
    setOpenId(null);
    if (id === null) return;
    // The row is still mounted; the panel is what unmounts.
    const button = document.getElementById(`vq-open-${id}`);
    button?.focus();
  }, [openId]);

  const columns: readonly DataTableColumn<VerificationQueueItem>[] = [
    {
      /* CLM-86 — the selection tick, and after a bulk call the row's own result. */
      id: 'select',
      header: '',
      cellClassName: 'tick',
      cell: (item) => {
        /* CLM-108 (R-108-17) — settled by policy, terminal: no selection box, so it can never reach the bulk bar. */
        if (item.verification_status === 'AutoApproved') {
          return (
            <span className="mut" title="Settled by policy — view only">
              <span aria-hidden="true">—</span>
              <span className="vh">Settled by policy — not selectable</span>
            </span>
          );
        }
        const result = bulk.results.get(item.verification_id);
        return (
          <>
            <input
              type="checkbox"
              checked={selection.isSelected(item.verification_id)}
              disabled={bulk.busy}
              aria-label={`Select ${item.field_name} on ${item.contract_key}`}
              onChange={(event) => {
                selection.set([item.verification_id], event.target.checked);
              }}
            />
            {result === undefined ? null : (
              <>
                <br />
                <ResultBadge result={result} />
              </>
            )}
          </>
        );
      },
    },
    {
      id: 'contract',
      header: 'Contract',
      cell: (item) => (
        <>
          {/* CLM-89 (R-89-1) — the compact SharePoint link beside the key; the full label is the aria-label. */}
          <code>{item.contract_key}</code> <OpenContractLink url={item.sharepoint_item_url ?? null} compact />
          <br />
          <span className="mut">
            {item.primary_party_display_name} ·{' '}
            {/* CLM-110 (R-110-23) — provisional until an administrator confirms it; drives no rule here. */}
            <ContractTypeTag type={item.contract_type} confirmation={item.contract_type_confirmation} />
            {/* CLM-99 — an explicit "Not billable" state when the field is
             * actually known, never a guess when an older response omits it
             * entirely (primary_party_is_billable is optional/nullable). */}
            {item.primary_party_is_billable === true ? (
              <>
                {' '}
                · billing id <code>{item.primary_party_billing_client_id}</code>
              </>
            ) : item.primary_party_is_billable === false ? (
              <> · Not billable</>
            ) : null}
          </span>
        </>
      ),
    },
    {
      id: 'field',
      header: 'Field',
      cell: (item) => (
        <>
          <code>{item.field_name}</code>
          {/* CLM-78 — the hold's counterparty-name row: provided vs found in the document, decided by Legal. */}
          {item.field_name === COUNTERPARTY_NAME_FIELD ? (
            <>
              <br />
              <span className="tag out">provided vs found · Legal decides</span>
            </>
          ) : null}
          {/* CLM-85 (D-85-8) — the key_terms.* rows of a tables_missing contract, and (CLM-109) the
              admin-defined additional_terms.* rows: short commercial terms a rate card can feed. */}
          {tablesMissingApplies(item) ? (
            <>
              <br />
              <span className="tag acc">tables not detected — check the value against the PDF</span>
            </>
          ) : null}
          {/* CLM-109 (R-109-15) — what the model was asked for this admin field, and whether it is retired. */}
          {isAdminFieldName(item.field_name) && item.field_description != null ? (
            <>
              <br />
              <span className="mut" data-testid="admin-field-description">
                {askedWithCopy(item.field_description)}
              </span>
            </>
          ) : null}
          {item.field_retired === true ? (
            <>
              <br />
              <span className="tag out" title={RETIRED_COPY} data-testid="admin-field-retired">
                retired
              </span>
            </>
          ) : null}
        </>
      ),
    },
    {
      id: 'value',
      header: 'Extracted value',
      cell: (item) =>
        item.field_over_bound_length != null ? (
          /* CLM-109 (R-109-12) — empty because it was over its bound, not because the document is silent. */
          <span className="mut" data-testid="admin-field-over-bound">
            <span className="tag bad">over its bound</span> {overBoundCopy(item.field_over_bound_length)}
          </span>
        ) : item.extracted_value === null ? (
          <span className="mut">The pipeline found no value</span>
        ) : (
          item.extracted_value
        ),
    },
    {
      id: 'stakes',
      header: 'Stakes',
      cell: (item) =>
        item.is_high_stakes ? (
          // The word, not only the colour: this is what puts a row under an SLA.
          <span className="tag bad">High stakes</span>
        ) : (
          <span className="tag out">Standard</span>
        ),
    },
    {
      id: 'aging',
      header: 'Age',
      cell: (item) => (
        <AgingSignal
          age_days={item.age_days}
          aging_signal={item.aging_signal}
          escalated_utc={item.escalated_utc}
          is_escalated={item.is_escalated}
        />
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item) => <VerificationStatusLabel status={item.verification_status} />,
    },
    {
      id: 'provenance',
      header: 'Provenance',
      cell: (item) => <ProvenanceChip provenance={item.provenance} />,
    },
  ];

  const totals = state.response?.totals ?? null;
  const page = state.response?.page ?? null;

  return (
    <>
      <div className="hd">
        <div>
          <h2>{surface.label}</h2>
          <p>{surface.summary}</p>
        </div>
      </div>

      {totals ? (
        <div className="totals">
          <div className="total">
            <span>Pending</span>
            <b>{totals.pending}</b>
          </div>
          <div className="total">
            <span>Pending, high stakes</span>
            <b>{totals.pending_high_stakes}</b>
          </div>
          <div className="total" data-tone={totals.escalated > 0 ? 'bad' : 'plain'}>
            <span>Escalated</span>
            <b>{totals.escalated}</b>
          </div>
          <div className="total">
            <span>Oldest pending, days</span>
            <b>
              {totals.oldest_pending_age_days === null ? '—' : totals.oldest_pending_age_days}
            </b>
          </div>
        </div>
      ) : null}

      <div className="card">
        {/* CLM-108 — the heading says what the rows are: the queue, the policy-settled list, or a status filter. */}
        <h3>
          {status === null
            ? 'Fields awaiting verification'
            : onlyAutoApproved
              ? 'Fields settled by policy'
              : 'Fields with the chosen status'}
        </h3>
        <p>
          Confirming a field is an assertion about one model output under one extraction schema
          version — the provenance column says which. Every filter below is sent to the API, which
          re-derives your scope from your token on each call; nothing is filtered in this browser,
          so the counts above and the rows below describe the same set.
        </p>

        {/* CLM-108 (R-108-17) — opened on `?status=`: say what the rows are, and how to get back to the queue. */}
        {status === null ? null : (
          <div className="banner up" role="status" data-testid="queue-status-filter">
            {onlyAutoApproved ? (
              <>
                <p className="notice-head">
                  <strong>Settled by policy — view only</strong>
                </p>
                <p>
                  These values were auto-approved by policy (CR-06) and were not reviewed by a person. They stay
                  auto-approved when the policy changes; nothing here can settle them again, and they cannot be
                  selected.
                </p>
              </>
            ) : (
              <p>
                Showing rows with status {status.map((value) => verificationStatusLabel(value)).join(', ')} — not the
                pending queue.
                {/* A mixed filter: the view-only words are about the policy-settled rows, never the page. */}
                {includesAutoApproved
                  ? ' Rows settled by policy are view only: they were not reviewed by a person and cannot be selected. The others can be reviewed.'
                  : null}
              </p>
            )}
            <button type="button" className="btn sm" onClick={showPendingQueue}>
              Show the pending queue
            </button>
          </div>
        )}

        <div className="vfilters">
          {/* CLM-86 (D-86-5) — four row narrowings, ANDed server-side after the scope predicate. */}
          <label className="check" htmlFor="vq-party">
            Client
          </label>
          <PartyPicker
            id="vq-party"
            label="Client"
            value={party?.party_key ?? ''}
            initialParties={party === null ? [] : [party]}
            emptyLabel="All clients"
            onChange={(picked) => {
              setParty(picked);
              resetPaging();
            }}
          />
          <label className="check">
            Field
            <select
              aria-label="Field"
              value={fieldName}
              onChange={(event) => {
                setFieldName(event.target.value);
                dropUrlFilter('field_name');
                resetPaging();
              }}
            >
              <option value="">All fields</option>
              {QUEUE_FIELD_NAMES.map((name) => (
                <option key={name} value={name}>
                  {fieldLabel(name)}
                </option>
              ))}
              {/* CLM-108 — a linked field outside the governed list (an admin field) still shows what is sent. */}
              {fieldName !== '' && !(QUEUE_FIELD_NAMES as readonly string[]).includes(fieldName) ? (
                <option value={fieldName}>{fieldName}</option>
              ) : null}
            </select>
          </label>
          <label className="check">
            Type
            <select
              aria-label="Contract type"
              value={contractType}
              onChange={(event) => {
                setContractType(event.target.value as '' | ContractType);
                resetPaging();
              }}
            >
              <option value="">All types</option>
              {CONTRACT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          <label className="check" htmlFor="vq-tags">
            Tags (all of)
          </label>
          <TagInput
            id="vq-tags"
            mode="filter"
            value={tags}
            onChange={(next) => {
              setTags(next);
              resetPaging();
            }}
          />
          <label className="check">
            <input
              type="checkbox"
              checked={highStakesOnly}
              onChange={(event) => {
                setHighStakesOnly(event.target.checked);
                resetPaging();
              }}
            />
            High-stakes fields only
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={escalatedOnly}
              onChange={(event) => {
                setEscalatedOnly(event.target.checked);
                resetPaging();
              }}
            />
            Escalated only
          </label>
        </div>

        {state.problem ? (
          <ErrorNotice
            kind={state.problem.kind}
            detail={state.problem.detail}
            correlation_id={state.problem.correlation_id}
          />
        ) : (
          <>
            {/* CLM-86 (D-86-1) — a sibling above the table, never a slot (D-69-2). Select all is this page.
                CLM-108 — over the selectable rows only; on a page of policy-settled rows there is no bulk bar at all. */}
            {includesAutoApproved && selectableItems.length === 0 ? null : (
              <>
            <SelectionToolbar
              label="Verification queue"
              noun="fields"
              count={selection.count}
              total={selectableItems.length}
              disabled={state.busy || bulk.busy}
              onSelectAll={() => {
                selection.selectAll();
              }}
              onClear={selection.reset}
            >
              {rejecting ? (
                <span className="confirm vq-reject">
                  <label htmlFor="vq-bulk-reason">Why these fields cannot be settled</label>
                  <textarea
                    id="vq-bulk-reason"
                    rows={2}
                    maxLength={1000}
                    value={rejectReason}
                    disabled={bulk.busy}
                    onChange={(event) => {
                      setRejectReason(event.target.value);
                      setRejectError(null);
                    }}
                  />
                  <button
                    type="button"
                    className="btn sm bad"
                    disabled={bulk.busy || selection.count === 0}
                    onClick={onRejectSelected}
                  >
                    Reject {selection.count.toLocaleString()} {selection.count === 1 ? 'field' : 'fields'}
                  </button>
                  <button
                    type="button"
                    className="btn sm qt"
                    disabled={bulk.busy}
                    onClick={() => {
                      setRejecting(false);
                      setRejectReason('');
                      setRejectError(null);
                    }}
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn sm"
                    disabled={!mayBulk || bulk.busy || selection.count === 0}
                    {...(mayBulk ? {} : { title: BULK_ROLE_HINT })}
                    onClick={() => {
                      void runBulk('confirm');
                    }}
                  >
                    Confirm selected
                  </button>
                  <button
                    type="button"
                    className="btn sm qt"
                    disabled={!mayBulk || bulk.busy || selection.count === 0}
                    {...(mayBulk ? {} : { title: BULK_ROLE_HINT })}
                    onClick={() => {
                      setRejecting(true);
                    }}
                  >
                    Reject selected…
                  </button>
                </>
              )}
            </SelectionToolbar>
            <p className="mut vq-caption">
              Select all selects the rows on this page. Confirm records each selected field as
              Verified; Reject records one reason against each. Every row is settled on its own —
              a row that cannot be settled is reported and the rest still land.
            </p>
              </>
            )}
            {rejectError === null ? null : (
              <p className="vq-error" role="alert">
                {rejectError}
              </p>
            )}
            {bulk.problem ? (
              <ErrorNotice
                kind={bulk.problem.kind}
                detail={bulk.problem.detail}
                correlation_id={bulk.problem.correlation_id}
              />
            ) : null}
            {bulk.counts ? (
              <p
                className="vq-summary"
                role="status"
                title={`${bulk.counts.not_pending.toLocaleString()} not pending, ${bulk.counts.not_found.toLocaleString()} not found, ${bulk.counts.failed.toLocaleString()} failed`}
              >
                {bulk.counts.settled.toLocaleString()} settled, {bulk.counts.not_permitted.toLocaleString()}{' '}
                skipped (not permitted),{' '}
                {(bulk.counts.not_pending + bulk.counts.not_found + bulk.counts.failed).toLocaleString()} failed
              </p>
            ) : null}
            <DataTable
              caption={
                status === null
                  ? 'Fields awaiting human verification, oldest first'
                  : onlyAutoApproved
                    ? 'Fields settled by policy, view only, oldest first'
                    : `Fields with status ${status.map((value) => verificationStatusLabel(value)).join(', ')}, oldest first`
              }
              columns={columns}
              rows={items}
              rowKey={(item) => item.verification_id}
              isCurrent={(item) => item.verification_id === openId}
              busy={state.busy}
              activate={{
                header: 'Review',
                /* CLM-108 — a policy-settled row opens read-only, and its button says so. */
                label: (item) => (item.verification_status === 'AutoApproved' ? 'View' : 'Review'),
                buttonId: rowButtonId,
                onActivate: (item) => {
                  setOpenId(item.verification_id);
                },
              }}
              empty={
                onlyAutoApproved ? (
                  <p>
                    <strong>No value in your scope was auto-approved{fieldName === '' ? '' : ` for ${fieldName}`}.</strong>
                  </p>
                ) : status !== null ? (
                  <p>
                    <strong>No field in your scope has the chosen status.</strong>
                  </p>
                ) : (
                  <>
                    <p>
                      <strong>Nothing is awaiting verification in your scope.</strong>
                    </p>
                    <p>
                      That is either an empty queue or an empty scope, and this page cannot tell you
                      which — the query is incapable of returning a field outside your scope, so
                      there is nothing to report about what lies beyond it.
                    </p>
                  </>
                )
              }
            />

            {page ? (
              <CursorPager
                next_cursor={page.next_cursor}
                historyDepth={stack.cursors.length}
                busy={state.busy}
                pageNumber={pageNumber(stack)}
                returnedCount={page.returned_count}
                totalInScope={page.total_in_scope}
                onNext={() => {
                  const next = page.next_cursor;
                  if (next === null) return;
                  setStack((current) => pushCursor(current, next));
                  setCursor(next);
                  setOpenId(null);
                  selection.reset();
                  clearBulk();
                }}
                onPrevious={() => {
                  const popped = popCursor(stack);
                  setStack(popped.stack);
                  setCursor(popped.cursor);
                  setOpenId(null);
                  selection.reset();
                  clearBulk();
                }}
              />
            ) : null}
          </>
        )}
      </div>

      {openItem ? (
        <VerifyPanel
          item={openItem}
          onClose={closePanel}
          onSettled={() => {
            // Re-fetch the same page rather than mutating the row in place: the
            // response's `remaining_pending_count` and the totals above are
            // server-counted, and a locally patched row would disagree with them.
            setReloadNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}
